import React, { useEffect, useRef } from 'react';
import './Services.css';
import { Link, useLocation } from 'react-router-dom';
import { trackCTAClick } from '../utils/analytics';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  FaFileInvoiceDollar,
  FaChartLine,
  FaRobot,
  FaCogs,
  FaLightbulb,
  FaLaptop,
  FaUsersCog,
  FaArrowRight,
  FaCheckCircle,
} from 'react-icons/fa';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: reduceMotion ? 0 : 0.45, delay: reduceMotion ? 0 : delay, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

const ServiceImage = ({ src, alt, variant = '', loading = 'lazy', fetchPriority }) => (
  <figure className={`solution-image ${variant}`.trim()}>
    <img src={src} alt={alt} loading={loading} decoding="async" fetchPriority={fetchPriority} />
  </figure>
);

function Services() {
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (location.hash) {
      // Reset to top so scroll position from previous page doesn't affect calculation
      window.scrollTo(0, 0);
      const timer = setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [location]);

  return (
    <div className="solutions-page">
      <Helmet>
        <title>IT Solutions & Services - SpocoDoc, Automation, Data Analytics | Media Mars</title>
        <meta name="description" content="Explore Media Mars solutions: SpocoDoc budget & invoice management, business process automation, Power BI data analytics, system integration, IT strategy reviews, and custom app development for UK SMEs." />
        <meta name="keywords" content="SpocoDoc, budget management software, invoice approval software UK, Power BI consulting, business process automation UK, ERP CRM integration, IT strategy review, custom app development, Power Platform development, Azure automation" />
        <meta property="og:title" content="IT Solutions & Services | Media Mars" />
        <meta property="og:description" content="SpocoDoc platform, automation, data analytics, system integration and IT strategy - solving real business problems for UK organisations." />
        <meta property="og:url" content="https://www.media-mars.com/services" />
        <meta property="og:image" content="https://www.media-mars.com/MediaMarsLogo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="IT Solutions & Services | Media Mars" />
        <meta name="twitter:description" content="SpocoDoc platform, automation, data analytics, system integration and IT strategy for UK businesses." />
        <meta name="twitter:image" content="https://www.media-mars.com/MediaMarsLogo.png" />
        <link rel="canonical" href="https://www.media-mars.com/services" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "provider": { "@type": "Organization", "name": "Media Mars Limited" },
            "serviceType": ["SpocoDoc SaaS Platform", "Business Process Automation", "Data Analytics & Power BI", "System Integration", "IT Strategy Consulting", "Custom Software Development", "Hardware Supply", "IT Staffing"],
            "areaServed": ["GB", "EU", "IN"],
            "url": "https://www.media-mars.com/services"
          })}
        </script>
      </Helmet>

      {/* HERO */}
      <section className="solutions-hero">
        <div className="solutions-hero__inner">
          <motion.div
            initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.45, ease: 'easeOut' }}
          >
            <span className="solutions-hero__tag">How We Help</span>
            <h1>We Don't Sell Services.<br /><span className="solutions-hero__accent">We Solve Problems.</span></h1>
            <p>Every engagement starts with understanding what's holding your business back. Then we design, build, and deliver the right solution - whether that's our SaaS platform, custom automation, or a strategic roadmap.</p>
          </motion.div>
          <ServiceImage
            src="/Images/Services/ServicesBannerImage.png"
            alt="Abstract Media Mars services banner showing connected technology systems"
            variant="solution-image--hero"
            loading="eager"
            fetchPriority="high"
          />
        </div>
      </section>

      {/* IT STRATEGY */}
      <section className="sol-block" id="it-strategy">
        <div className="sol-block__inner">
          <FadeIn className="sol-block__text">
            <span className="sol-tag">CHALLENGE 01</span>
            <h2><FaLightbulb className="sol-icon" /> IT Costs Growing Without Clear ROI?</h2>
            <p className="sol-problem"><strong>The problem:</strong> Licences you don't use, vendors you overpay, infrastructure that doesn't scale. Nobody can tell the CEO or CFO whether IT investment is driving the business forward.</p>
            <p>Our independent strategy reviews give leadership full visibility - a clear picture of what's working, what's wasted, and a prioritised roadmap toward measurable ROI.</p>
            <ul className="sol-list">
              <li>Technology alignment with business goals and growth plans</li>
              <li>IT team structure, capability gaps, and succession planning</li>
              <li>Tools, platforms, licences, and vendor contract review</li>
              <li>Risk identification, cost-saving opportunities, and quick wins</li>
              <li>Coaching and guidance for IT leadership teams</li>
            </ul>
            <Link to="/contact" className="btn-primary" onClick={() => trackCTAClick('Schedule a Strategy Review', 'it-strategy')}>Schedule a Strategy Review <FaArrowRight /></Link>
          </FadeIn>
          <FadeIn className="sol-block__visual" delay={0.2}>
            <ServiceImage src="/Images/Services/challenge01.png" alt="Executive technology strategy planning interface" />
          </FadeIn>
        </div>
      </section>

      {/* AUTOMATION */}
      <section className="sol-block alt" id="automation">
        <div className="sol-block__inner reverse">
          <FadeIn className="sol-block__text">
            <span className="sol-tag">CHALLENGE 02</span>
            <h2><FaRobot className="sol-icon" /> Wasting Hours on Tasks That Should Be Automated?</h2>
            <p className="sol-problem"><strong>The problem:</strong> Manual data entry, copy-pasting between systems, chasing approvals by email - your team's best hours disappear into repetitive tasks.</p>
            <p>We find the highest-impact automation opportunities in your business and build smart workflows that save hours every week - with measurable results from day one.</p>
            <ul className="sol-list">
              <li>Eliminate repetitive manual tasks and reduce human error</li>
              <li>Accelerate approvals, service delivery, and data synchronisation</li>
              <li>Use Power Automate for quick low-code workflows</li>
              <li>Deploy Azure Functions and Logic Apps where scale matters</li>
              <li>Automate across finance, HR, operations, and IT</li>
            </ul>
            <Link to="/contact" className="btn-primary" onClick={() => trackCTAClick('Automate My Processes', 'automation')}>Automate My Processes <FaArrowRight /></Link>
          </FadeIn>
          <FadeIn className="sol-block__visual" delay={0.2}>
            <ServiceImage src="/Images/Services/challenge02.png" alt="Enterprise automation workflow from trigger to business outcome" />
          </FadeIn>
        </div>
      </section>

      {/* DATA & ANALYTICS */}
      <section className="sol-block" id="data-analytics">
        <div className="sol-block__inner">
          <FadeIn className="sol-block__text">
            <span className="sol-tag">CHALLENGE 03</span>
            <h2><FaChartLine className="sol-icon" /> Making Decisions Without Reliable Data?</h2>
            <p className="sol-problem"><strong>The problem:</strong> Data trapped in spreadsheets, CRM, ERP, emails, and cloud drives. Reports are manual, inconsistent, and outdated before they're finished.</p>
            <p>We connect all your data sources into a single, trusted pipeline - and deliver live dashboards that let leaders act on facts, not assumptions.</p>
            <ul className="sol-list">
              <li>Integrate data from Excel, CRM/ERP, Microsoft 365, APIs, and cloud services</li>
              <li>Build cloud data warehouses (Azure SQL, Synapse) as your single source of truth</li>
              <li>Create Power BI dashboards for executives, sales, finance, and operations</li>
              <li>Replace error-prone spreadsheets with automated, real-time reporting</li>
              <li>Feed live data into B2B platforms, eCommerce portals, and stock tools</li>
            </ul>
            <Link to="/contact" className="btn-primary" onClick={() => trackCTAClick('Talk Data Strategy', 'data-analytics')}>Talk Data Strategy <FaArrowRight /></Link>
          </FadeIn>
          <FadeIn className="sol-block__visual" delay={0.2}>
            <ServiceImage src="/Images/Services/challenge03.png" alt="Modern analytics dashboard and connected data pipeline" />
          </FadeIn>
        </div>
      </section>

      {/* INTEGRATION */}
      <section className="sol-block alt" id="integration">
        <div className="sol-block__inner reverse">
          <FadeIn className="sol-block__text">
            <span className="sol-tag">CHALLENGE 04</span>
            <h2><FaCogs className="sol-icon" /> Systems That Don't Talk to Each Other?</h2>
            <p className="sol-problem"><strong>The problem:</strong> Your ERP, CRM, eCommerce, and finance systems are disconnected. Every handover is a risk. Teams re-key data and nobody has a single view.</p>
            <p>We connect your entire digital ecosystem with secure, reliable integrations - eliminating double entry and giving every department a live, trusted view of the business.</p>
            <ul className="sol-list">
              <li>Connect ERP, CRM, eCommerce, finance, HR, and supplier systems</li>
              <li>Use APIs, Azure Logic Apps, Data Factory, or direct SQL pipelines</li>
              <li>Create secure cloud + on-premise hybrid architectures</li>
              <li>Expose live data across departments and partners</li>
              <li>From Shopify+ERP to CRM+Outlook - we've done it all</li>
            </ul>
            <Link to="/contact" className="btn-primary" onClick={() => trackCTAClick('Request Integration Review', 'integration')}>Request Integration Review <FaArrowRight /></Link>
          </FadeIn>
          <FadeIn className="sol-block__visual" delay={0.2}>
            <ServiceImage src="/Images/Services/challenge04.png" alt="System integration architecture connecting ERP, CRM, eCommerce and finance" variant="solution-image--dark" />
          </FadeIn>
        </div>
      </section>

      {/* CUSTOM APPS */}
      <section className="sol-block" id="apps">
        <div className="sol-block__inner">
          <FadeIn className="sol-block__text">
            <span className="sol-tag">CHALLENGE 05</span>
            <h2><FaCogs className="sol-icon" /> Need Software Built for How You Actually Work?</h2>
            <p className="sol-problem"><strong>The problem:</strong> Off-the-shelf tools don't fit your workflows. You end up with workarounds, shadow IT, and frustrated teams.</p>
            <p>We develop custom solutions - from low-code Power Apps to full cloud applications - that match your real processes. No compromise, no workarounds.</p>
            <ul className="sol-list">
              <li>Budget trackers, asset management, request and approval tools</li>
              <li>Field service apps, site visit reporting, warranty portals</li>
              <li>Product catalogues with live pricing and ERP/B2B integration</li>
              <li>HR onboarding, compliance tracking, team scheduling</li>
              <li>Built on Power Platform, Azure, React, .NET - whatever fits best</li>
            </ul>
            <Link to="/contact" className="btn-primary" onClick={() => trackCTAClick('Discuss Custom Development', 'custom-apps')}>Discuss Custom Development <FaArrowRight /></Link>
          </FadeIn>
          <FadeIn className="sol-block__visual" delay={0.2}>
            <ServiceImage src="/Images/Services/challenge05.png" alt="Custom business application design across browser and mobile interfaces" />
          </FadeIn>
        </div>
      </section>

      {/* SPOCODOC */}
      <section className="sol-block alt" id="spocodoc">
        <div className="sol-block__inner reverse">
          <FadeIn className="sol-block__text">
            <span className="sol-tag product">OUR PRODUCT</span>
            <h2><FaFileInvoiceDollar className="sol-icon" /> SpocoDoc - Budget & Invoice Management Platform</h2>
            <p className="sol-problem"><strong>The problem:</strong> Invoices approved on paper, budgets in spreadsheets, expenses submitted by email - finance teams drowning in manual work with zero real-time visibility.</p>
            <p><strong>SpocoDoc</strong> is our SaaS platform purpose-built to solve this. It unifies budgets, purchase invoices, expenses, and multi-level approvals in one digital workspace.</p>
            <div className="sol-features-grid">
              <div className="sol-feat"><FaCheckCircle className="sol-feat-check" /> Budget tracking by department, project, or cost centre</div>
              <div className="sol-feat"><FaCheckCircle className="sol-feat-check" /> Digital invoice upload, matching, and routing</div>
              <div className="sol-feat"><FaCheckCircle className="sol-feat-check" /> Mobile expense submission and one-click approval</div>
              <div className="sol-feat"><FaCheckCircle className="sol-feat-check" /> Multi-level rule-based approval workflows</div>
              <div className="sol-feat"><FaCheckCircle className="sol-feat-check" /> Full audit trail and compliance reporting</div>
              <div className="sol-feat"><FaCheckCircle className="sol-feat-check" /> Real-time dashboards for finance leaders</div>
            </div>
            <Link to="/contact" className="btn-primary" onClick={() => trackCTAClick('Request a Demo', 'spocodoc')}>Request a Demo <FaArrowRight /></Link>
          </FadeIn>
          <FadeIn className="sol-block__visual" delay={0.2}>
            <ServiceImage src="/Images/Services/OurProduct.png" alt="SpocoDoc product interface for budget, invoice and approval management" variant="solution-image--product" />
          </FadeIn>
        </div>
      </section>

      {/* HARDWARE & SUPPLY */}
      <section className="sol-block" id="supply">
        <div className="sol-block__inner sol-compact">
          <FadeIn className="sol-block__text">
            <span className="sol-tag">ALSO</span>
            <h2><FaLaptop className="sol-icon" /> Hardware & Software Supply</h2>
            <p>Need devices deployed across offices? We handle end-to-end logistics - laptops, peripherals, licences, and Microsoft Autopilot setup - across the UK, EU, and India. From purchase to plug-in.</p>
            <Link to="/contact" className="link-arrow" onClick={() => trackCTAClick('Discuss Supply Options', 'hardware-supply')}>Discuss Supply Options <FaArrowRight /></Link>
          </FadeIn>
        </div>
      </section>

      {/* EXPERTS */}
      <section className="sol-block alt" id="experts">
        <div className="sol-block__inner sol-compact">
          <FadeIn className="sol-block__text">
            <span className="sol-tag">ALSO</span>
            <h2><FaUsersCog className="sol-icon" /> On-Demand IT Expertise</h2>
            <p>Scale your team with vetted UK-based and offshore professionals - cloud engineers, data specialists, security experts, interim CIOs. Flexible contracts, fast onboarding, no hiring delays.</p>
            <Link to="/contact" className="link-arrow" onClick={() => trackCTAClick('Find Your Expert', 'on-demand-expertise')}>Find Your Expert <FaArrowRight /></Link>
          </FadeIn>
        </div>
      </section>

      {/* CTA */}
      <section className="solutions-cta">
        <FadeIn>
          <h2>Let's Talk About Your Challenge</h2>
          <p>Every business is different. Tell us what's not working - we'll show you how to fix it.</p>
          <Link to="/contact" className="btn-primary large" onClick={() => trackCTAClick('Get in Touch', 'services-cta')}>Get in Touch <FaArrowRight /></Link>
        </FadeIn>
      </section>
    </div>
  );
}

export default Services;
